import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import './Login.css';

export default function Login() {
  const { login, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect to the home page if already logged in
  if (user) {
    navigate('/', { replace: true });
  }

  /* ─── Email/Password Login ─── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.email.trim()) {
      setError('Email is required.');
      return;
    }
    if (!form.password) {
      setError('Password is required.');
      return;
    }

    setLoading(true);
    try {
      await login(form.email.trim().toLowerCase(), form.password);
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'Invalid email or password.'
      );
    } finally {
      setLoading(false);
    }
  };

  /* ─── Google Login ─── */
  const handleGoogleLogin = async (credentialResponse) => {
    setError('');
    setLoading(true);
    try {
      await loginWithGoogle(credentialResponse.credential);
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.error ||
        'Google sign-in failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">

        {/* ─── Logo ─── */}
        <div className="auth-brand">
          <Link to="/" className="auth-logo">
            VK<span>TREND</span>
          </Link>
        </div>

        {/* ─── Title ─── */}
        <h1 className="auth-title">Sign in to VKTrend</h1>

        {/* ─── Error ─── */}
        {error && <div className="auth-error">{error}</div>}

        {/* ─── Form ─── */}
        <form onSubmit={handleSubmit} className="auth-form">
          <label className="auth-label" htmlFor="login-email">
            Email
          </label>
          <input
            id="login-email"
            type="email"
            className="auth-input"
            placeholder=""
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            autoComplete="email"
            autoFocus
            disabled={loading}
          />

          <label className="auth-label" htmlFor="login-password">
            Password
          </label>
          <input
            id="login-password"
            type="password"
            className="auth-input"
            placeholder=""
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            autoComplete="current-password"
            disabled={loading}
          />

          <button
            type="submit"
            className="auth-btn auth-btn-primary"
            disabled={loading}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {/* ─── Divider ─── */}
        <div className="auth-divider">
          <span>or</span>
        </div>

        {/* ─── Google Button ─── */}
        <div className="auth-google">
          <GoogleLogin
            onSuccess={handleGoogleLogin}
            onError={() => setError('Google sign-in failed.')}
            theme="outline"
            size="large"
            text="continue_with"
            shape="rectangular"
          />
        </div>

        {/* ─── Footer ─── */}
        <p className="auth-footer">
          New to VKTrend?{' '}
          <Link to="/register">Create an account</Link>
        </p>
      </div>
    </div>
  );
}